import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Paper,
  Grid,
  Typography,
  Button,
  IconButton,
  Box,
  CardMedia,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import { InventoryContext } from 'Context/InventoryContext';
import { ShopContext } from 'Context/ShopContext';
import ItemService from 'services/ItemService';
import ShopsHelper from 'helpers/ShopsHelper';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ITEM_TYPES, SUBSCRIPTION_TYPE, ROW_MATERIAL_STOCK_UPDATE_OPTION } from 'const';
import ItemDialog from 'components/popups/ItemDialog';
import EditBatchExpiryDialog from 'components/popups/EditBatchExpiryDialog';
import EditIcon from '@mui/icons-material/Edit';
import { WarehouseContext } from 'Context/WarehouseContext';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import styles from './styles.module.css';
import 'react-quill/dist/quill.snow.css';

const descriptionOverrideStyles = `
  .ql-editor ol,
  .ql-editor ul {
    padding-left: 1.5em !important;
  }
  .ql-editor li {
    list-style-type: inherit !important;
  }
`;

const DetailRow = ({ label, value, color, fullWidth }) => (
  <Box
    display="flex"
    flexDirection={fullWidth ? 'column' : 'row'}
    justifyContent="space-between"
    py={1.5}
    borderBottom="1px solid #f1f1f1"
  >
    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500, marginBottom: fullWidth ? '4px' : 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      style={{
        fontWeight: 600,
        color: color || '#2c3e50',
        textAlign: fullWidth ? 'left' : 'right',
      }}
    >
      {value !== null && value !== undefined && value !== '' ? value : '-'}
    </Typography>
  </Box>
);

const SectionTitle = ({ title, icon }) => (
  <Box mt={2} mb={1} display="flex" alignItems="center">
    <Typography
      variant="subtitle1"
      style={{
        fontWeight: 800,
        color: '#1d57ac',
        textTransform: 'uppercase',
        fontSize: '0.85rem',
        letterSpacing: '1px',
      }}
    >
      {title}
    </Typography>
    <Box flexGrow={1} ml={2} height="1px" bgcolor="#e0e0e0" />
  </Box>
);
const formatDuration = (hours) => {
  if (hours === null || hours === undefined || hours === '') return '-';
  const h = Number(hours);
  if (isNaN(h)) return hours;
  if (h >= 24) {
    const days = h / 24;
    return `${days} ${days === 1 ? 'Day' : 'Days'}`;
  }
  return `${h} ${h === 1 ? 'Hour' : 'Hours'}`;
};

const ItemDetails = () => {
  const { itemId } = useParams();
  const history = useHistory();
  const { t: tCommon } = useTranslation('common');
  const {
    products,
    categories,
    chargeGroups,
    priceCategories,
    getPriceCategories,
    getItemPriceCategories,
    getItemAddonMappings,
    getProductVariantMapping,
    getVariantLabels,
    getVariantDimensions,
    itemPriceCategories: contextItemPriceCategories,
    itemAddonMappings: contextAddonMappings,
    productVariantMapping: contextProductVariantMapping,
    variantLabels,
    variantDimensions,
    baseProducts,
    getBaseProducts,
    getStockBatches,
    stockBatches,
    setStockBatches,
  } = useContext(InventoryContext);
  const { shop } = useContext(ShopContext);
  const { getWarehouseProducts, warehouseProducts, getWarehouses, warehouses } = useContext(WarehouseContext);

  const [item, setItem] = useState(null);
  const [itemAdvanced, setItemAdvanced] = useState({});
  const [itemAddons, setItemAddons] = useState([]);
  const [isEditBatchDialogOpen, setIsEditBatchDialogOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [itemPriceCategories, setItemPriceCategories] = useState([]);
  const [variants, setVariants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isAdvancedLoading, setIsAdvancedLoading] = useState(false);

  const fetchItemDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      // Find item in context first
      const foundItem = (products || []).find((p) => String(p.id) === String(itemId));
      if (foundItem) {
        setItem(foundItem);
      }

      // Fetch all necessary context data
      await Promise.all([
        getPriceCategories(),
        getItemPriceCategories(),
        getItemAddonMappings(),
        getProductVariantMapping(),
        getVariantLabels(),
        getVariantDimensions(),
        getBaseProducts(),
      ]);

      if (shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM) {
        await getStockBatches();
      }
    } catch (error) {
      console.error('Failed to fetch item details:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    getPriceCategories,
    getStockBatches,
    itemId,
    products,
    getItemPriceCategories,
    getItemAddonMappings,
    getProductVariantMapping,
    getVariantLabels,
    getVariantDimensions,
    getBaseProducts,
    shop?.subscription?.type,
  ]);

  const handleToggleAdvanced = async () => {
    if (!showAdvanced && Object.keys(itemAdvanced).length === 0) {
      setIsAdvancedLoading(true);
      try {
        const advanced = await ItemService.getItemAdvanced(itemId);
        setItemAdvanced(advanced?.data || advanced || {});
      } catch (error) {
        console.error('Failed to fetch advanced details:', error);
      } finally {
        setIsAdvancedLoading(false);
      }
    }
    setShowAdvanced(!showAdvanced);
  };

  useEffect(() => {
    fetchItemDetails();
  }, [fetchItemDetails]);

  useEffect(() => {
    if (item?.details) {
      setItemAdvanced(item.details?.data || item.details || {});
    }
  }, [item]);

  useEffect(() => {
    if (!featureIsExpired(shop?.warehouse_validity)) {
      getWarehouseProducts({ useCache: true });
      getWarehouses();
    }
  }, [shop?.warehouse_validity, getWarehouseProducts, getWarehouses]);

  useEffect(() => {
    if (!item) return;

    // Filter Price Categories
    const filteredPC = (contextItemPriceCategories || []).filter((p) => String(p.item) === String(item.id));
    setItemPriceCategories(filteredPC);

    // Filter Addons
    const filteredAddons = (contextAddonMappings || [])
      .filter((a) => String(a.item) === String(item.id))
      .map((a) => {
        const addonItem = (products || []).find((i) => String(i.id) === String(a.addon_item));
        return { ...a, addonItemName: addonItem ? addonItem.name : a.addon_item };
      });
    setItemAddons(filteredAddons);

    // Filter Variants
    const mappingsForItem = (contextProductVariantMapping || []).filter((m) => String(m.item) === String(item.id));
    const v = mappingsForItem.map((m) => {
      const label = (variantLabels || []).find((l) => String(l.id) === String(m.label));
      const dimension = (variantDimensions || []).find((d) => String(d.id) === String(label?.dimension));
      return {
        ...m,
        label: label,
        dimension: dimension,
        baseProduct: (baseProducts || []).find((bp) => String(bp.id) === String(m.product)),
      };
    });
    setVariants(v);
  }, [
    item,
    contextItemPriceCategories,
    contextAddonMappings,
    contextProductVariantMapping,
    variantLabels,
    variantDimensions,
    baseProducts,
    products,
  ]);

  const productOptions = useMemo(() => {
    if (!item?.config?.product_options) return [];
    return item.config.product_options;
  }, [item]);

  const itemWarehouseStocks = useMemo(() => {
    if (!Array.isArray(warehouseProducts)) return [];
    return warehouseProducts.filter((wp) => String(wp.item?.id || wp.item) === String(itemId));
  }, [warehouseProducts, itemId]);

  const groupedPriceCategoryItems = useMemo(() => {
    const grouped = (itemPriceCategories || []).reduce((acc, curr) => {
      const catId = curr.category?.id || curr.category_id;
      if (!acc[catId]) {
        acc[catId] = {
          category: curr.category || (priceCategories || []).find((c) => c.id === catId),
          items: [],
        };
      }
      acc[catId].items.push(curr);
      return acc;
    }, {});
    return Object.values(grouped).map((g) => ({
      ...g,
      items: g.items.sort((a, b) => (a.minimum_quantity || 0) - (b.minimum_quantity || 0)),
    }));
  }, [itemPriceCategories, priceCategories]);

  const categoryName = useMemo(() => {
    if (!item?.category) return '-';
    const cat = (categories || []).find((c) => String(c.id) === String(item.category));
    return cat ? cat.name : item.category;
  }, [item, categories]);

  const chargeGroupName = useMemo(() => {
    if (!item?.charge_group) return '-';
    const group = (chargeGroups || []).find((g) => String(g.id) === String(item.charge_group));
    return group ? group.name : '-';
  }, [item, chargeGroups]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!item) {
    return (
      <Box p={3}>
        <Typography variant="h6">{tCommon('Item not found')}</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.goBack()}>
          {tCommon('Back to list')}
        </Button>
      </Box>
    );
  }

  return (
    <div className={styles.contentWrapperSm}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {' '}
          {tCommon('Item')}
          <span className={styles.menuTitle}>{tCommon('details')}</span>
        </h2>
      </div>

      <div className={styles.changeableSec}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={() => history.goBack()} style={{ marginRight: '10px' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" style={{ fontWeight: 700 }}>
              {item.name}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => setIsEditDialogOpen(true)}
            style={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
          >
            {tCommon('Edit')}
          </Button>
        </Box>

        <Paper elevation={0} variant="outlined" style={{ padding: '24px', borderRadius: '8px' }}>
          <Grid container spacing={4}>
            {/* Left Column: Image and Basic Info */}
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <CardMedia
                  component="img"
                  image={item.image || '/placeholder.png'}
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    height: '280px',
                    objectFit: 'contain',
                    borderRadius: '12px',
                    border: '1px solid #eee',
                    marginBottom: '16px',
                    backgroundColor: '#fff',
                  }}
                />

                <Typography variant="h6" align="center" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  {item.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  PLU: {item.plu}
                </Typography>
                <Box mt={2} width="100%">
                  <DetailRow
                    label={tCommon('Status')}
                    value={item.is_available ? tCommon('Available') : tCommon('Not Available')}
                    color={item.is_available ? '#2ecc71' : '#e74c3c'}
                  />
                  <DetailRow label={tCommon('Type')} value={item.type} />
                  <DetailRow label={tCommon('Category')} value={categoryName} />
                  <DetailRow label={tCommon('PLU')} value={item.plu} />
                  <DetailRow label={tCommon('Internal Name')} value={item.internal_name} />
                </Box>
              </Box>
            </Grid>

            {/* Right Column: Pricing and Advanced Details */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={4}>
                <Grid item xs={12} sm={6}>
                  <SectionTitle title={tCommon('Pricing & Stock')} />
                  <DetailRow
                    label={tCommon('Price')}
                    value={ShopsHelper.getAmountFormatted(shop, item.price)}
                    color="#1d57ac"
                  />
                  <DetailRow label={tCommon('MRP')} value={ShopsHelper.getAmountFormatted(shop, item.mrp)} />
                  <DetailRow label={tCommon('Cost')} value={ShopsHelper.getAmountFormatted(shop, item.cost)} />
                  <DetailRow
                    label={tCommon('Profit Margin (%)')}
                    value={item?.config?.profit_margin ? `${item.config.profit_margin}%` : '0%'}
                  />
                  <DetailRow label={tCommon('Unit')} value={item.unit} />
                  {(item.type === ITEM_TYPES.INVENTORY || item.type === ITEM_TYPES.PRODUCTION) && (
                    <DetailRow
                      label={tCommon('Current Stock')}
                      value={item.stock}
                      color={item.stock > 0 ? '#2ecc71' : '#e74c3c'}
                    />
                  )}
                  <DetailRow label={tCommon('Tax (%)')} value={item.tax} />
                  <DetailRow label={tCommon('Additional Tax (%)')} value={item.cess} />
                  <DetailRow
                    label={tCommon('Least Price')}
                    value={
                      item.least_price && item.least_price !== 0
                        ? ShopsHelper.getAmountFormatted(shop, item.least_price)
                        : '-'
                    }
                  />
                  <DetailRow label={tCommon('Stock On Hold')} value={item.stock_on_hold} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SectionTitle title={tCommon('Logistics & Config')} />
                  <DetailRow label={tCommon('HSN')} value={item.hsn} />
                  <DetailRow label={tCommon('Barcode (EAN)')} value={item.ean === 'nil' ? '-' : item.ean} />
                  <DetailRow label={tCommon('Order')} value={item.order} />
                  <DetailRow label={tCommon('USP Kg Ratio')} value={item.kg_ratio} />
                  <DetailRow label={tCommon('Charge Group')} value={chargeGroupName} />
                  <DetailRow label={tCommon('Low Stock Alert')} value={item.low_stock_alert_quantity} />
                  <DetailRow
                    label={tCommon('Machine Sync')}
                    value={item.sync_enabled ? tCommon('Yes') : tCommon('No')}
                  />
                  <DetailRow
                    label={tCommon('Available on Cart')}
                    value={item.is_available_on_cart ? tCommon('Yes') : tCommon('No')}
                    color={item.is_available_on_cart ? '#1d57ac' : '#e74c3c'}
                  />
                  <DetailRow
                    label={tCommon('Export to Scale')}
                    value={item.sync_enabled ? tCommon('Yes') : tCommon('No')}
                    color={item.sync_enabled ? '#1d57ac' : '#95a5a6'}
                  />
                  <DetailRow
                    label={tCommon('Batch Tracking Enabled')}
                    value={item.is_batch_enabled ? tCommon('Yes') : tCommon('No')}
                    color={item.is_batch_enabled ? '#1d57ac' : '#95a5a6'}
                  />
                  {item.is_batch_enabled && (
                    <>
                      <DetailRow
                        label={tCommon('Shelf Duration')}
                        value={formatDuration(item.shelf_duration_in_hours)}
                      />
                      <DetailRow
                        label={tCommon('Alert Before Expiry')}
                        value={formatDuration(item.expiry_alert_duration_in_hours)}
                      />
                      <DetailRow
                        label={tCommon('Expected Sold Out Duration')}
                        value={formatDuration(item.expected_sold_out_duration_in_hours)}
                      />
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* Product Batches */}
            {shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM && item.is_batch_enabled && (
              <Grid item xs={12}>
                <SectionTitle title={tCommon('Product Batches')} />
                <TableContainer
                  component={Paper}
                  elevation={0}
                  variant="outlined"
                  style={{ marginTop: '10px', borderRadius: '12px', overflow: 'hidden' }}
                >
                  <Table size="small">
                    <TableHead style={{ backgroundColor: '#f8f9fa' }}>
                      <TableRow>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('ID')}
                        </TableCell>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Batch Name')}
                        </TableCell>
                        {!featureIsExpired(shop?.warehouse_validity) && (
                          <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                            {tCommon('Warehouse')}
                          </TableCell>
                        )}
                        <TableCell align="right" style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Price')}
                        </TableCell>
                        <TableCell align="right" style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Stock')}
                        </TableCell>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Expiry Date')}
                        </TableCell>
                        <TableCell align="center" style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Action')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(stockBatches || [])
                        .filter((b) => String(b.item) === String(itemId))
                        .map((batch, idx) => {
                          const now = new Date();
                          const expiryDate = batch.expiry_date
                            ? new Date(
                                batch.expiry_date.endsWith('Z') ? batch.expiry_date.slice(0, -1) : batch.expiry_date
                              )
                            : null;
                          let bg = 'transparent';
                          if (expiryDate) {
                            if (expiryDate < now) {
                              bg = 'rgba(245, 39, 39, 0.2)';
                            } else if (item?.expiry_alert_duration_in_hours) {
                              const diffInHours = (expiryDate - now) / (1000 * 60 * 60);
                              if (diffInHours <= Number(item.expiry_alert_duration_in_hours)) {
                                bg = 'rgba(245, 169, 39, 0.2)';
                              }
                            }
                          }
                          const wh = !featureIsExpired(shop?.warehouse_validity)
                            ? (warehouses || []).find((w) => String(w.id) === String(batch.warehouse))
                            : null;
                          return (
                            <TableRow key={idx} hover style={{ backgroundColor: bg }}>
                              <TableCell style={{ padding: '12px 16px' }}>{batch.id}</TableCell>
                              <TableCell style={{ padding: '12px 16px', fontWeight: 500 }}>
                                {batch.batch_name}
                              </TableCell>
                              {!featureIsExpired(shop?.warehouse_validity) && (
                                <TableCell style={{ padding: '12px 16px' }}>{wh ? wh.name : '-'}</TableCell>
                              )}
                              <TableCell align="right" style={{ padding: '12px 16px' }}>
                                {ShopsHelper.getAmountFormatted(shop, batch.price ?? 0)}
                              </TableCell>
                              <TableCell align="right" style={{ padding: '12px 16px' }}>
                                {Number(batch.stock || 0).toFixed(3)}
                              </TableCell>
                              <TableCell style={{ padding: '12px 16px' }}>
                                {batch.expiry_date ? DateTimeHelpers.formatUTCDateTime(batch.expiry_date) : '-'}
                              </TableCell>
                              <TableCell align="center" style={{ padding: '12px 16px' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedBatch(batch);
                                    setIsEditBatchDialogOpen(true);
                                  }}
                                  color="primary"
                                >
                                  <EditIcon style={{ fontSize: '1.2rem' }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      {(stockBatches || []).filter((b) => String(b.item) === String(itemId)).length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={!featureIsExpired(shop?.warehouse_validity) ? 6 : 5}
                            align="center"
                            style={{ padding: '20px', color: '#999' }}
                          >
                            {tCommon('No batches found for this item.')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* Warehouse Stock */}
            {!featureIsExpired(shop?.warehouse_validity) && itemWarehouseStocks.length > 0 && (
              <Grid item xs={12}>
                <SectionTitle title={tCommon('Warehouse Stock')} />
                <TableContainer
                  component={Paper}
                  elevation={0}
                  variant="outlined"
                  style={{ marginTop: '10px', borderRadius: '12px', overflow: 'hidden' }}
                >
                  <Table size="small">
                    <TableHead style={{ backgroundColor: '#f8f9fa' }}>
                      <TableRow>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Warehouse')}
                        </TableCell>
                        <TableCell align="right" style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Stock')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemWarehouseStocks.map((ws, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell style={{ padding: '12px 16px', fontWeight: 500 }}>
                            {ws.warehouse?.name || ws.warehouse_name || '-'}
                          </TableCell>
                          <TableCell align="right" style={{ padding: '12px 16px', fontWeight: 600 }}>
                            {Number(ws.stock || 0).toFixed(3)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            <Grid item xs={12}>
              <SectionTitle title={tCommon('Description')} />
              {item.description ? (
                <div
                  style={{
                    color: '#636e72',
                    marginTop: '4px',
                    lineHeight: '1.6',
                    background: '#f8f9fa',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #edf2f7',
                  }}
                >
                  <style>{descriptionOverrideStyles}</style>
                  <div
                    className="ql-editor"
                    style={{ padding: 0 }}
                    dangerouslySetInnerHTML={{ __html: item.description }}
                  />
                </div>
              ) : (
                <Typography
                  variant="body2"
                  style={{
                    color: '#636e72',
                    marginTop: '4px',
                    lineHeight: '1.6',
                    background: '#f8f9fa',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #edf2f7',
                  }}
                >
                  {tCommon('No description available.')}
                </Typography>
              )}
            </Grid>

            {/* Schedules */}
            {(item.available_days || item.available_time_range) && (
              <Grid item xs={12}>
                <SectionTitle title={tCommon('Product Schedules')} />
                <DetailRow label={tCommon('Available Days')} value={item.available_days} />
                <DetailRow label={tCommon('Available Time')} value={item.available_time_range} />
              </Grid>
            )}

            {/* Tags and Other Names */}
            {(item.config?.tags || item.config?.other_names) && (
              <Grid item xs={12}>
                <SectionTitle title={tCommon('Additional Info')} />
                {item.config?.tags && Object.entries(item.config.tags).length > 0 && (
                  <Box mt={1}>
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
                      {tCommon('Tags')}:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                      {Object.entries(item.config.tags).map(([key, value]) => (
                        <Typography
                          key={key}
                          variant="caption"
                          style={{ background: '#f0f0f0', padding: '2px 8px', borderRadius: '4px' }}
                        >
                          {key}: {Array.isArray(value) ? value.join(', ') : value}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
                {item.config?.other_names && (
                  <Box mt={2}>
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
                      {tCommon('Other Names')}:
                    </Typography>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', marginTop: '4px' }}>
                      {typeof item.config.other_names === 'string'
                        ? item.config.other_names
                        : Object.entries(item.config.other_names)
                            .map(([k, v]) => `${k}${v ? ` | Option: ${v}` : ''}`)
                            .join('\n')}
                    </Typography>
                  </Box>
                )}
              </Grid>
            )}

            {/* Product Options */}
            {productOptions.length > 0 && (
              <Grid item xs={12}>
                <SectionTitle title={tCommon('Product Options')} />
                <TableContainer
                  component={Paper}
                  elevation={0}
                  variant="outlined"
                  style={{ marginTop: '10px', borderRadius: '12px', overflow: 'hidden' }}
                >
                  <Table size="small">
                    <TableHead style={{ backgroundColor: '#f8f9fa' }}>
                      <TableRow>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Label')}
                        </TableCell>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }} align="right">
                          {tCommon('Quantity Ratio')}
                        </TableCell>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }} align="right">
                          {tCommon('Price')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productOptions.map((opt, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell style={{ padding: '12px 16px', fontWeight: 500 }}>{opt.label}</TableCell>
                          <TableCell align="right" style={{ padding: '12px 16px' }}>
                            {opt.quantity_ratio}
                          </TableCell>
                          <TableCell align="right" style={{ padding: '12px 16px', fontWeight: 600 }}>
                            {ShopsHelper.getAmountFormatted(shop, opt.price)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* Sub items for Combo/Production */}
            {(item.type === ITEM_TYPES.COMBO || item.type === ITEM_TYPES.PRODUCTION) && (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <SectionTitle
                    title={
                      item.type === ITEM_TYPES.COMBO ? tCommon('Product Sub Items') : tCommon('Product Production Items')
                    }
                  />
                  {item.type === ITEM_TYPES.COMBO && (
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
                      {tCommon('Raw Material Stock Update Option')}:{' '}
                      {(() => {
                        const option = itemAdvanced.row_material_stock_update_option || ROW_MATERIAL_STOCK_UPDATE_OPTION.LIVE;
                        return (
                          <span style={{ color: option === ROW_MATERIAL_STOCK_UPDATE_OPTION.LIVE ? '#2e7d32' : '#1d57ac', fontWeight: 600 }}>
                            {option === ROW_MATERIAL_STOCK_UPDATE_OPTION.LIVE ? tCommon('Live') : tCommon('Consumption')}
                          </span>
                        );
                      })()}
                    </Typography>
                  )}
                </Box>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  variant="outlined"
                  style={{ marginTop: '10px', borderRadius: '12px', overflow: 'hidden' }}
                >
                  <Table size="small">
                    <TableHead style={{ backgroundColor: '#f8f9fa' }}>
                      <TableRow>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('PLU')}
                        </TableCell>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Item')}
                        </TableCell>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }} align="right">
                          {tCommon('Fraction')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const visibleSubItems = (itemAdvanced.sub_items || []).filter((si) => {
                          const subItem = (products || []).find(
                            (p) => String(p.id) === String(si.item_id) || (si.plu && String(p.plu) === String(si.plu))
                          );
                          return subItem && subItem.plu;
                        });
                        if (visibleSubItems.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={2} align="center" style={{ padding: '20px', color: '#999' }}>
                                {tCommon('No sub items defined.')}
                              </TableCell>
                            </TableRow>
                          );
                        }
                        return visibleSubItems.map((si, idx) => {
                          const subItem = (products || []).find(
                            (p) => String(p.id) === String(si.item_id) || (si.plu && String(p.plu) === String(si.plu))
                          );
                          return (
                            <TableRow key={idx} hover>
                              <TableCell style={{ padding: '12px 16px', fontWeight: 500 }}>{subItem?.plu}</TableCell>
                              <TableCell style={{ padding: '12px 16px', fontWeight: 500 }}>{subItem?.name}</TableCell>
                              <TableCell align="right" style={{ padding: '12px 16px' }}>
                                {si.fraction}
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* Price Categories */}
            {groupedPriceCategoryItems.length > 0 && (
              <Grid item xs={12}>
                <SectionTitle title={tCommon('Price Categories')} />
                <TableContainer
                  component={Paper}
                  elevation={0}
                  variant="outlined"
                  style={{ marginTop: '10px', borderRadius: '12px', overflow: 'hidden' }}
                >
                  <Table size="small">
                    <TableHead style={{ backgroundColor: '#f8f9fa' }}>
                      <TableRow>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Category')}
                        </TableCell>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Qty')}
                        </TableCell>
                        <TableCell align="right" style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Price')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedPriceCategoryItems.map((group) => {
                        const { category, items } = group;
                        return items.map((pc, index) => (
                          <TableRow key={pc.id || index} hover>
                            {index === 0 && (
                              <TableCell
                                rowSpan={items.length}
                                style={{ padding: '12px 16px', fontWeight: 600, borderRight: '1px solid #f1f1f1' }}
                              >
                                {category?.name || 'N/A'}
                              </TableCell>
                            )}
                            <TableCell style={{ padding: '12px 16px' }}>
                              {items.length === 1 && pc.minimum_quantity === 0
                                ? '0'
                                : index === items.length - 1
                                ? `${pc.minimum_quantity} +`
                                : `${pc.minimum_quantity} - ${items[index + 1].minimum_quantity}`}
                            </TableCell>
                            <TableCell align="right" style={{ padding: '12px 16px', fontWeight: 600 }}>
                              {ShopsHelper.getAmountFormatted(shop, pc.price)}
                            </TableCell>
                          </TableRow>
                        ));
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* Addons */}
            {itemAddons.length > 0 && (
              <Grid item xs={12}>
                <SectionTitle title={tCommon('Add-on Items')} />
                <TableContainer
                  component={Paper}
                  elevation={0}
                  variant="outlined"
                  style={{ marginTop: '10px', borderRadius: '12px', overflow: 'hidden' }}
                >
                  <Table size="small">
                    <TableHead style={{ backgroundColor: '#f8f9fa' }}>
                      <TableRow>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Add-on Item')}
                        </TableCell>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }} align="right">
                          {tCommon('Price Delta')}
                        </TableCell>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }} align="right">
                          {tCommon('Max Qty')}
                        </TableCell>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }} align="center">
                          {tCommon('Available')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemAddons.map((ao, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell style={{ padding: '12px 16px', fontWeight: 500 }}>{ao.addonItemName}</TableCell>
                          <TableCell align="right" style={{ padding: '12px 16px', fontWeight: 600 }}>
                            {ShopsHelper.getAmountFormatted(shop, ao.price_delta)}
                          </TableCell>
                          <TableCell align="right" style={{ padding: '12px 16px' }}>
                            {ao.max_quantity || '-'}
                          </TableCell>
                          <TableCell align="center" style={{ padding: '12px 16px' }}>
                            <Typography
                              variant="caption"
                              style={{
                                color: ao.is_available !== false ? '#2ecc71' : '#e74c3c',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: '0.65rem',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                backgroundColor:
                                  ao.is_available !== false ? 'rgba(46, 204, 113, 0.1)' : 'rgba(231, 76, 60, 0.1)',
                              }}
                            >
                              {ao.is_available !== false ? tCommon('Yes') : tCommon('No')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* Variants */}
            {variants.length > 0 && (
              <Grid item xs={12}>
                <SectionTitle title={tCommon('Product Variants')} />
                <TableContainer
                  component={Paper}
                  elevation={0}
                  variant="outlined"
                  style={{ marginTop: '10px', borderRadius: '12px', overflow: 'hidden' }}
                >
                  <Table size="small">
                    <TableHead style={{ backgroundColor: '#f8f9fa' }}>
                      <TableRow>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Dimension')}
                        </TableCell>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Label')}
                        </TableCell>
                        <TableCell style={{ fontWeight: 700, color: '#1d57ac', padding: '12px 16px' }}>
                          {tCommon('Base Product')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {variants.map((v, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell style={{ padding: '12px 16px', fontWeight: 500 }}>
                            {v.dimension?.name || '-'}
                          </TableCell>
                          <TableCell style={{ padding: '12px 16px' }}>{v.label?.name || '-'}</TableCell>
                          <TableCell style={{ padding: '12px 16px' }}>{v.baseProduct?.name || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* Advanced Details at the end */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={4} mb={2}>
                <Box display="flex" alignItems="center" flexGrow={1}>
                  <Typography
                    variant="subtitle1"
                    style={{
                      fontWeight: 800,
                      color: '#1d57ac',
                      textTransform: 'uppercase',
                      fontSize: '0.85rem',
                      letterSpacing: '1px',
                    }}
                  >
                    {tCommon('Advanced Details')}
                  </Typography>
                  <Box flexGrow={1} ml={2} height="1px" bgcolor="#e0e0e0" />
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={handleToggleAdvanced}
                  style={{ marginLeft: '16px', borderRadius: '20px', textTransform: 'none' }}
                  disabled={isAdvancedLoading}
                >
                  {isAdvancedLoading ? <CircularProgress size={16} style={{ marginRight: '8px' }} /> : null}
                  {showAdvanced ? tCommon('Hide') : tCommon('Show')}
                </Button>
              </Box>

              {showAdvanced && (
                <Grid container spacing={3}>
                  {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                    <Grid item xs={12} sm={4} key={num}>
                      <DetailRow label={`${tCommon('Text')} ${num}`} value={itemAdvanced[`text${num}`]} />
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={4}>
                    <DetailRow
                      label={tCommon('Min Quantity')}
                      value={
                        itemAdvanced.min_quantity && itemAdvanced.min_quantity !== 0 ? itemAdvanced.min_quantity : '-'
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DetailRow
                      label={tCommon('Max Quantity')}
                      value={
                        itemAdvanced.max_quantity && itemAdvanced.max_quantity !== 0 ? itemAdvanced.max_quantity : '-'
                      }
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Paper>
      </div>

      {isEditDialogOpen && (
        <ItemDialog
          item={item}
          handleClose={() => {
            setIsEditDialogOpen(false);
            fetchItemDetails();
          }}
        />
      )}

      {isEditBatchDialogOpen && selectedBatch && (
        <EditBatchExpiryDialog
          batch={selectedBatch}
          handleClose={() => {
            setIsEditBatchDialogOpen(false);
            setSelectedBatch(null);
          }}
          handleSuccess={(updatedBatch) => {
            setStockBatches((prevBatches) =>
              (prevBatches || []).map((b) => (String(b.id) === String(updatedBatch.id) ? updatedBatch : b))
            );
            setIsEditBatchDialogOpen(false);
            setSelectedBatch(null);
          }}
        />
      )}
    </div>
  );
};

export default withConsoleBase(ItemDetails);
